import { useEffect, useRef, useState } from "react";
import { reconcilePayment } from "@/api";
import { CheckCircle2, Loader2, Clock, RefreshCw, X, ShieldCheck } from "lucide-react";

/**
 * ActivationBanner (v7.12)
 *
 * Shown on the customer dashboard right after a Razorpay checkout succeeds
 * while the subscription is still being activated. Polls
 * POST /api/customer/reconcile-payment, which asks Razorpay directly whether
 * the order has a captured payment — so activation never depends on the
 * webhook or on the backend verify call.
 *
 * States: hidden | activating | activated | delayed
 */
export const PENDING_PAYMENT_KEY = "rebuilt_payment_pending";
const POLL_MS = 5000;
const MAX_POLLS = 36;              // ~3 minutes of active polling
const FLAG_TTL_MS = 24 * 3600e3;   // ignore flags older than 24h

export function markPaymentPending(info) {
  try {
    localStorage.setItem(PENDING_PAYMENT_KEY, JSON.stringify({ ...info, at: Date.now() }));
  } catch { /* storage unavailable (private mode) */ }
}

export function clearPaymentPending() {
  try { localStorage.removeItem(PENDING_PAYMENT_KEY); } catch { /* noop */ }
}

function readPendingFlag() {
  try {
    const raw = localStorage.getItem(PENDING_PAYMENT_KEY);
    if (!raw) return null;
    const flag = JSON.parse(raw);
    if (!flag?.at || Date.now() - flag.at > FLAG_TTL_MS) {
      localStorage.removeItem(PENDING_PAYMENT_KEY);
      return null;
    }
    return flag;
  } catch { return null; }
}

export default function ActivationBanner({ subscription, onActivated }) {
  const [state, setState] = useState("hidden");
  const [flag, setFlag] = useState(null);
  const pollsRef = useRef(0);
  const timerRef = useRef(null);
  const doneRef = useRef(false);

  const isPending = !!subscription && !subscription.is_active && subscription.payment_status === "pending";

  const stop = () => { if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; } };

  const handleActivated = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    stop();
    clearPaymentPending();
    setState("activated");
    onActivated?.();
  };

  const checkOnce = async () => {
    try {
      const res = await reconcilePayment();
      const status = res?.data?.status;
      if (status === "success") { handleActivated(); return true; }
    } catch { /* transient — keep polling */ }
    return false;
  };

  const poll = async () => {
    if (doneRef.current) return;
    pollsRef.current += 1;
    const ok = await checkOnce();
    if (ok || doneRef.current) return;
    if (pollsRef.current >= MAX_POLLS) { setState("delayed"); return; }
    timerRef.current = setTimeout(poll, POLL_MS);
  };

  const startPolling = () => {
    stop();
    pollsRef.current = 0;
    setState("activating");
    poll();
  };

  useEffect(() => {
    // Already active → clean up any stale flag, nothing to show.
    if (subscription?.is_active) {
      if (readPendingFlag() && !doneRef.current) {
        // Flag present + active = we just activated (e.g. verify-payment worked
        // after navigation). Show a brief success confirmation.
        clearPaymentPending();
        doneRef.current = true;
        setState("activated");
      }
      return undefined;
    }
    if (!isPending) return undefined;

    const f = readPendingFlag();
    setFlag(f);
    if (f) {
      startPolling();
    } else {
      // No local flag (paid from another device / closed the browser) — do one
      // silent check. If Razorpay says it's captured we still show success.
      checkOnce();
    }
    return stop;
  }, [subscription?.id, subscription?.is_active, subscription?.payment_status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-hide the success state after a few seconds.
  useEffect(() => {
    if (state !== "activated") return undefined;
    const t = setTimeout(() => setState("hidden"), 8000);
    return () => clearTimeout(t);
  }, [state]);

  if (state === "hidden") return null;

  if (state === "activated") {
    return (
      <div
        data-testid="activation-banner"
        data-state="activated"
        className="rounded-2xl p-4 text-white shadow-lg relative overflow-hidden fade-in"
        style={{ background: "linear-gradient(135deg, #10B981 0%, #059669 100%)" }}
      >
        <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-white/10" />
        <div className="relative flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
            <CheckCircle2 className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm" data-testid="activation-banner-title">Plan activated — you're all set!</p>
            <p className="text-xs text-white/85 mt-0.5">Your EMI Support Plan is live. You can now apply for loan assistance.</p>
          </div>
          <button
            onClick={() => setState("hidden")}
            data-testid="activation-banner-dismiss"
            className="p-1 rounded-lg text-white/80 hover:text-white hover:bg-white/15 transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  if (state === "delayed") {
    return (
      <div
        data-testid="activation-banner"
        data-state="delayed"
        className="rounded-2xl p-4 bg-amber-50 border border-amber-200 shadow-sm"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
            <Clock className="h-5 w-5 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-amber-900" data-testid="activation-banner-title">Activation is taking a little longer</p>
            <p className="text-xs text-amber-800 mt-0.5 leading-relaxed">
              Your payment is safe. We'll activate your plan automatically — no need to pay again.
              {flag?.payment_id && <> Payment ID: <span className="font-mono">{flag.payment_id}</span>.</>}
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <button
                onClick={startPolling}
                data-testid="activation-banner-retry"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 transition-colors"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Check again
              </button>
              <a
                href="mailto:support@rebuiltindia.co"
                className="text-xs font-medium text-amber-800 underline underline-offset-2 hover:text-amber-900"
              >
                Contact support
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // activating
  return (
    <div
      data-testid="activation-banner"
      data-state="activating"
      className="rounded-2xl p-4 text-white shadow-lg relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #0A2540 0%, #1565C0 100%)" }}
    >
      <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-cyan-400/15" />
      <div className="relative flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
          <Loader2 className="h-5 w-5 animate-spin text-cyan-300" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm" data-testid="activation-banner-title">Payment received — activating your plan</p>
          <p className="text-xs text-white/75 mt-0.5 flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-cyan-300" />
            Confirming with our payment partner. This usually takes a few seconds.
          </p>
          <div className="mt-3 h-1 rounded-full bg-white/15 overflow-hidden">
            <div className="h-full w-1/3 rounded-full bg-cyan-300 activation-progress" />
          </div>
        </div>
      </div>
    </div>
  );
}
